/**
 * Person avatar with an initials fallback.
 *
 * The fallback tint is derived from a hash of the name, so the same intern keeps
 * the same colour on every screen without the server having to store one. Red is
 * deliberately excluded from the palette: a red avatar reads as an error.
 */
import { forwardRef, useState, type HTMLAttributes } from 'react';
import type { BadgeTone } from '@aforium/shared';
import { cn } from '../../lib/cn';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

const SIZE_CLASS: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-2xs',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
};

const TONE_CLASS: Record<Exclude<BadgeTone, 'danger'>, string> = {
  neutral: 'bg-neutral-soft text-neutral-on-soft',
  info: 'bg-info-soft text-info-on-soft',
  success: 'bg-success-soft text-success-on-soft',
  warning: 'bg-warning-soft text-warning-on-soft',
  accent: 'bg-accent-soft text-accent-on-soft',
};

const FALLBACK_TONES = Object.keys(TONE_CLASS) as Array<Exclude<BadgeTone, 'danger'>>;

/** Stable, order-independent string hash — same name always lands on same tone. */
function hashName(name: string): number {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = (hash * 31 + name.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
}

/** "Ada Lovelace" -> "AL", "cher" -> "C", "" -> "?". */
export function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return '?';
  }
  const first = words[0]?.charAt(0) ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.charAt(0) ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

export interface AvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  name: string;
  src?: string;
  size?: AvatarSize;
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { className, name, src, size = 'md', ...props },
  ref,
) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;
  const tone = FALLBACK_TONES[hashName(name) % FALLBACK_TONES.length] ?? 'neutral';

  return (
    <span
      ref={ref}
      role="img"
      aria-label={name}
      title={name}
      className={cn(
        'relative inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-semibold uppercase',
        SIZE_CLASS[size],
        showImage ? 'bg-surface-sunken' : TONE_CLASS[tone],
        className,
      )}
      {...props}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <span aria-hidden="true">{getInitials(name)}</span>
      )}
    </span>
  );
});

export interface AvatarGroupProps extends HTMLAttributes<HTMLDivElement> {
  avatars: Array<{ name: string; src?: string }>;
  /** How many avatars to show before collapsing the rest into "+N". */
  max?: number;
  size?: AvatarSize;
}

export const AvatarGroup = forwardRef<HTMLDivElement, AvatarGroupProps>(function AvatarGroup(
  { className, avatars, max = 4, size = 'sm', ...props },
  ref,
) {
  const visible = avatars.slice(0, Math.max(1, max));
  const overflow = avatars.slice(visible.length);

  return (
    <div ref={ref} className={cn('flex items-center -space-x-2', className)} {...props}>
      {visible.map((person) => (
        <Avatar
          key={`${person.name}-${person.src ?? ''}`}
          name={person.name}
          src={person.src}
          size={size}
          className="ring-2 ring-surface"
        />
      ))}
      {overflow.length > 0 ? (
        <span
          title={overflow.map((person) => person.name).join(', ')}
          className={cn(
            'relative inline-flex shrink-0 items-center justify-center rounded-full bg-surface-sunken font-semibold text-content-muted ring-2 ring-surface',
            SIZE_CLASS[size],
          )}
        >
          <span aria-hidden="true">+{overflow.length}</span>
          <span className="sr-only">{`${overflow.length} more: ${overflow.map((person) => person.name).join(', ')}`}</span>
        </span>
      ) : null}
    </div>
  );
});
